import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { 
    IonContent, IonGrid, IonRow, IonCol
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { MarvTabsComponent, MarvTabComponent, MarvTab, MarvButtonComponent, MarvSelectItem } from '@marv/components';
import { MarvDateService, MarvStringService, MarvJsonService } from '@marv/services';
import { PhotoGalleryFiltersAllComponent, PhotoGalleryFiltersAnimaComponent, PhotoGalleryFiltersPearlsComponent, AnimaOperationFilters } from '@app/components';
import { 
    Photo, OperationCustomerPlanningSetting, OperationCard, InstantPhotoFilterTag, AnimaPhotoSearchCriteria, 
    PearlsPhotoSearchCriteria, PearlsPhotoProductCategory 
} from '@app/models';

export class PhotoFiltersValues { 
   
    startDate: Date = MarvDateService.GetFirstDayOfWeek();
    endDate: Date = MarvDateService.GetLastDayOfWeek();
    typesMissionsCodes: Array<string>;
    operationsIds: Array<string>;
    areasIds: Array<number>;
    sectorsIds: Array<number>;
    agentsIds: Array<number>;
    retailersCodes: Array<string>; 
    retailersItems: Array<MarvSelectItem> = [];
    storesCodes: Array<string>;
    storesItems: Array<MarvSelectItem> = []; 
    city: string;
    keywords: string;
    skipPresenceCheck: boolean;
    skipDrac: boolean;  
    animaPhotoFiltersValues: Map<number, AnimaPhotoFiltersValues> = new Map();
    pearlsOperationId:number;   
    pearlsPhotoFiltersValues: Map<number, PearlsPhotoFiltersValues> = new Map();
    nbFiltersFilled: number = 0;
    
    viewSelected: 'group'|'mozaic' = 'group';
    sizeSelected: 'small'|'medium'|'big' = 'medium';    
    keywordsCleaned: Array<string> = [];
    instantFiltersCount: Map<string, number> = new Map();
    instantFiltersCountSubject: Subject<Map<string, number>> = new Subject();
    instantFiltersCleaned: Array<string> = [];
    missionId: number;
    logaPictureTypes = ['PCHECK', 'COACHING', 'ANIMA_TAG_PHOTO', 'ANIMA_PRODUCT_PHOTO', 'ANIMA_ISA_PHOTO'];
        
    savingFilters: boolean = true;
    
    constructor(fromStorage: boolean = false, savingFilters: boolean = true, defaultView: 'group'|'mozaic' = 'group', defaultSize: 'small'|'medium'|'big' = 'medium') {        
        let planningFiltersValues:any = this;
        if(fromStorage){
            planningFiltersValues = this.loadFilters();
        }
        else{
            planningFiltersValues.viewSelected = defaultView;
            planningFiltersValues.sizeSelected = defaultSize;
        }
        planningFiltersValues.savingFilters = savingFilters;
        return planningFiltersValues;
    }
    
    loadFilters(): PhotoFiltersValues{     
        let planningFiltersValues: any = new PhotoFiltersValues();
        let temp = sessionStorage.getItem("photo_filters_values");
        if(temp){                     
            let filtersValues = MarvJsonService.Parse(temp);           
            Object.getOwnPropertyNames(filtersValues).forEach(k => {
                if(typeof filtersValues[k] !== 'undefined'){
                    if(planningFiltersValues[k] instanceof Date){
                        planningFiltersValues[k] = new Date(filtersValues[k]);
                    }   
                    else if(k === 'animaPhotoFiltersValues'){
                        let animaPhotoFiltersValues: Map<number, AnimaPhotoFiltersValues> = new Map();
                        filtersValues[k].forEach((value: any, key: number) => {
                            let newV: any = new AnimaPhotoFiltersValues();
                            Object.getOwnPropertyNames(value).forEach(prop => {
                                newV[prop] = value[prop];
                            });
                            animaPhotoFiltersValues.set(key, newV);
                        });
                        planningFiltersValues.animaPhotoFiltersValues = animaPhotoFiltersValues;
                    }                
                    else if(k === 'pearlsPhotoFiltersValues'){
                        let pearlsPhotoFiltersValues: Map<number, PearlsPhotoFiltersValues> = new Map();
                        filtersValues[k].forEach((value: any, key: number) => {
                            let newV: any = new PearlsPhotoFiltersValues();
                            Object.getOwnPropertyNames(value).forEach(prop => {
                                newV[prop] = value[prop];
                            });
                            pearlsPhotoFiltersValues.set(key, newV);
                        });
                        planningFiltersValues.pearlsPhotoFiltersValues = pearlsPhotoFiltersValues;
                    }                
                    else{
                        planningFiltersValues[k] = filtersValues[k];
                    }
                }
            });
        }
        return planningFiltersValues;
    }
        
    saveFilters(remove: boolean = false){    
        if(this.savingFilters){
            if(remove){    
                PhotoFiltersValues.DeleteFiltersSaved();
            }
            else{                   
                sessionStorage.setItem("photo_filters_values", MarvJsonService.Stringify(this));                
            }
        }
    }
    
    static DeleteFiltersSaved(){
        sessionStorage.removeItem("photo_filters_values");
    }
    
    static FromForm(form: FormGroup, retailersItems: Array<MarvSelectItem>, storesItems: Array<MarvSelectItem>, instantPhotoFilterTags: Array<InstantPhotoFilterTag>, animaPhotoSearchCriterias: Map<number, AnimaOperationFilters>, pearlsPhotoSearchCriteria: Map<number, PearlsPhotoSearchCriteria>, savingFilters: boolean = true, defaultView: 'group'|'mozaic' = 'group', defaultSize: 'small'|'medium'|'big' = 'medium'): PhotoFiltersValues{
        let filters = new PhotoFiltersValues(false, savingFilters, defaultView, defaultSize);
        
        if(form.value?.startDate){
            filters.startDate = form.value.startDate;
        }
        
        if(form.value?.endDate){
            filters.endDate = form.value.endDate;
        }
        
        if(form.value?.typesMissionsCodes && form.value.typesMissionsCodes.length > 0){
            filters.typesMissionsCodes = form.value.typesMissionsCodes;
            filters.nbFiltersFilled++;
        }
        if(form.value?.operationsIds && form.value.operationsIds.length > 0){
            filters.operationsIds = form.value.operationsIds;
            filters.nbFiltersFilled++;
        }        
        if(form.value?.areasIds && form.value.areasIds.length > 0){
            filters.areasIds = form.value.areasIds;
            filters.nbFiltersFilled++;
        }
        if(form.value?.sectorsIds && form.value.sectorsIds.length > 0){
            filters.sectorsIds = form.value.sectorsIds;
            filters.nbFiltersFilled++;
        }
        if(form.value?.retailersCodes && form.value.retailersCodes.length > 0){
            filters.retailersCodes = form.value.retailersCodes;
            filters.retailersItems = retailersItems;
            filters.nbFiltersFilled++;            
        }
        if(form.value?.storesCodes && form.value.storesCodes.length > 0){
            filters.storesCodes = form.value.storesCodes;
            filters.storesItems = storesItems;
            filters.nbFiltersFilled++;
        }
        if(form.value?.city && form.value.city != ''){
            filters.city = form.value.city;
            filters.nbFiltersFilled++;
        }
        if(form.value?.keywords && form.value.keywords != ''){
            filters.keywords = form.value.keywords;
            filters.nbFiltersFilled++;
        }
        
        if(instantPhotoFilterTags?.length > 0){
            let instantFiltersCleaned: Array<string> = [];
            instantPhotoFilterTags.forEach((value, index) => {
                if(form.value['instantPhotoFilterTag_'+index] === true){
                    instantFiltersCleaned.push(value.normalizedTag);
                    filters.nbFiltersFilled++;
                }
            });
            filters.instantFiltersCleaned = instantFiltersCleaned;
        }
                
        filters.skipPresenceCheck = false;
        if(form.value?.skipPresenceCheck){
            filters.skipPresenceCheck = true;
            filters.nbFiltersFilled++;
        }
        
        filters.skipDrac = false;
        if(form.value?.skipDrac){
            filters.skipDrac = true;
            filters.nbFiltersFilled++;
        }
        
        if(animaPhotoSearchCriterias.size > 0){
            let animaPhotoFiltersValues: Map<number, AnimaPhotoFiltersValues> = new Map<number, AnimaPhotoFiltersValues>;
            animaPhotoSearchCriterias.forEach((opFilters, index) => {
                
                let photoTags: Array<number> = [];                
                if(opFilters.filters.photoTagCriteriaChunks?.length > 0){
                    for(let rows of opFilters.filters.photoTagCriteriaChunks){
                        for(let tag of rows){
                            if(form.value['anima_photoTag_'+index+'_'+tag.value]){
                                photoTags.push(tag.value);
                                filters.nbFiltersFilled++;
                            }   
                        }
                    }
                }
                
                let isaReportQuestions: Array<number> = [];
                if(opFilters.filters.isaReportDataModelGroups?.length > 0){
                    for(let group of opFilters.filters.isaReportDataModelGroups){
                        for(let rows of group.isaReportDataModelChunks){
                            for(let question of rows){
                                if(form.value['anima_photoQuestion_'+index+'_'+question.id]){
                                    isaReportQuestions.push(question.id);
                                    filters.nbFiltersFilled++;
                                }
                            }
                        }
                    }
                }
                
                let productQuestions: Array<number> = [];
                if(opFilters.filters.productDataModelsChunks?.length > 0){
                    for(let rows of opFilters.filters.productDataModelsChunks){
                        for(let question of rows){
                            if(form.value['anima_photoProductQuestion_'+index+'_'+question.value]){
                                productQuestions.push(question.value);
                                filters.nbFiltersFilled++;
                            }  
                        }
                    }
                }
                
                let products: Array<number> = [];
                if(opFilters.filters.productCategories?.length > 0){
                    for(let category of opFilters.filters.productCategories){
                        for(let range of category.ranges){
                            for(let rows of range.productChunks){
                                for(let product of rows){
                                    if(form.value['anima_photoProduct_'+index+'_'+product.id]){
                                        products.push(product.id);
                                        filters.nbFiltersFilled++;
                                    }
                                }
                            }
                        }
                    }
                }                
                
                let v = new AnimaPhotoFiltersValues();
                
                if(form.value['anima_enableTaggedPictures_'+index] === false){
                    v.skipTaggedPictures = true;
                    filters.nbFiltersFilled++;
                }
                
                if(form.value['anima_enableIsaReportPictures_'+index] === false){
                    v.skipIsaReportPictures = true;
                    filters.nbFiltersFilled++;
                }
                
                if(form.value['anima_enableProductPictures_'+index] === false){
                    v.skipProductPictures = true;
                    filters.nbFiltersFilled++;
                }
                
                v.photoTags = photoTags;
                v.isaReportQuestions = isaReportQuestions;
                v.productQuestions = productQuestions;
                v.products = products;
                animaPhotoFiltersValues.set(index, v);
            });
            filters.animaPhotoFiltersValues = animaPhotoFiltersValues;
        }
                
        if(form.value?.currentPearlsOperationId){
            filters.pearlsOperationId = form.value.currentPearlsOperationId;
        }     
        
        if(pearlsPhotoSearchCriteria.size > 0){
            let pearlsPhotoFiltersValues: Map<number, PearlsPhotoFiltersValues> = new Map<number, PearlsPhotoFiltersValues>;
            pearlsPhotoSearchCriteria.forEach((photoSearchCriteria, index) => {
                
                let photoTags: Array<number> = [];                
                if(photoSearchCriteria.photoTagCriteriaChunks?.length > 0){
                    for(let rows of photoSearchCriteria.photoTagCriteriaChunks){
                        for(let tag of rows){
                            if(form.value['pearls_photoTag_'+index+'_'+tag.id]){
                                photoTags.push(tag.id);
                                filters.nbFiltersFilled++;
                            }   
                        }
                    }
                }
                
                let productQuestions: Array<number> = [];
                if(photoSearchCriteria.prDmPhotoCriteriaChunks?.length > 0){
                    for(let rows of photoSearchCriteria.prDmPhotoCriteriaChunks){
                        for(let question of rows){
                            if(form.value['pearls_photoProductQuestion_'+index+'_'+question.id]){
                                productQuestions.push(question.id);
                                filters.nbFiltersFilled++;
                            }  
                        }
                    }
                }
                
                
                let posmQuestions: Array<number> = [];
                if(photoSearchCriteria.poDmPhotoCriteriaChunks?.length > 0){
                    for(let rows of photoSearchCriteria.poDmPhotoCriteriaChunks){
                        for(let question of rows){
                            if(form.value['pearls_photoPosmQuestion_'+index+'_'+question.id]){
                                posmQuestions.push(question.id);
                                filters.nbFiltersFilled++;
                            }  
                        }
                    }
                }
                
                let products: Array<number> = [];
                let posms: Array<number> = [];
                if(photoSearchCriteria.productCategories && photoSearchCriteria.productCategories.productCategory?.length > 0){
                    for(let category of photoSearchCriteria.productCategories.productCategory){
                        for(let range of category.ranges){
                            if(category.hasProducts){
                                for(let rows of range.productChunks){
                                    for(let product of rows){
                                        if(form.value['pearls_photoProduct_'+index+'_'+product.id]){
                                            products.push(product.id);
                                            filters.nbFiltersFilled++;
                                        }                                    
                                    }
                                }
                            }
                            if(category.hasPosms){
                                for(let rows of range.posmChunks){
                                    for(let product of rows){
                                        if(form.value['pearls_photoPosm_'+index+'_'+product.id]){
                                            posms.push(product.id);
                                            filters.nbFiltersFilled++;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }     
                
                let storeCheckDataModels: Array<number> = [];
                if(photoSearchCriteria.scDmPhotoCriteriaChunks && photoSearchCriteria.scDmPhotoCriteriaChunks.length > 0){
                    for(let structure of photoSearchCriteria.scDmPhotoCriteriaChunks){                      
                        for(let storeCheckData of structure.storeCheckDataModels){            
                            if(form.value['pearls_photoStoreCheck_'+index+'_'+storeCheckData.id]){
                                storeCheckDataModels.push(storeCheckData.id);
                                filters.nbFiltersFilled++;
                            }
                        }
                    }
                }   
                
                let v = new PearlsPhotoFiltersValues();
                
                if(form.value['pearls_enableTaggedPictures_'+index] === false){
                    v.skipTaggedPictures = true;
                    filters.nbFiltersFilled++;
                }
                
                if(form.value['pearls_enableProductPictures_'+index] === false){
                    v.skipProductPictures = true;
                    filters.nbFiltersFilled++;
                }
                
                if(form.value['pearls_enablePosmPictures_'+index] === false){
                    v.skipPosmPictures = true;
                    filters.nbFiltersFilled++;
                }
                
                if(form.value['pearls_enableStoreCheckDataPictures_'+index] === false){
                    v.skipStoreCheckDataPictures = true;
                    filters.nbFiltersFilled++;
                }
                
                if(form.value['pearls_enableRangePictures_'+index] === false){
                    v.skipRangePictures = true;
                    filters.nbFiltersFilled++;
                }
                
                if(form.value['pearls_enableTaskPictures_'+index] === false){
                    v.skipTaskPictures = true;
                    filters.nbFiltersFilled++;
                }
                
                if(form.value['pearls_enableStitchedPhotos_'+index] === false){
                    v.skipStitchedPhotos = true;
                    filters.nbFiltersFilled++;
                }
                
                v.photoTags = photoTags;
                v.productQuestions = productQuestions;
                v.posmQuestions = posmQuestions;
                v.products = products;
                v.posms = posms;
                v.storeCheckDataModels = storeCheckDataModels;
                
                pearlsPhotoFiltersValues.set(index, v);
            });
            filters.pearlsPhotoFiltersValues = pearlsPhotoFiltersValues;
        }
        
        return filters;
    }
        
    getPictureTypes(operationId: number) {
        const clonedArray = Object.assign([], this.logaPictureTypes);
        const animaFilterModel = this.animaPhotoFiltersValues.get(operationId);
        if (animaFilterModel) {
            animaFilterModel.checkPictureTypes(clonedArray);
        }

        if (this.skipPresenceCheck) {
            PhotoFiltersValues.removePictureType(clonedArray, 'PCHECK');
        }
        if (this.skipDrac) {
            PhotoFiltersValues.removePictureType(clonedArray, 'COACHING');
        }

        return clonedArray;
    }
    
    static removePictureType(logaPictureTypes: string[], type: string) {
        const index = logaPictureTypes.indexOf(type);
        if (index > -1) {
            logaPictureTypes.splice(index, 1);
        }
    }
        
    filterPhoto(photo: Photo, customerPlanningSetting?: OperationCustomerPlanningSetting | undefined) {
        
        let checkSource: boolean = true;
        let checkKeywords: boolean = true;
        let checkInstantFilters: boolean = true;
        
        if (customerPlanningSetting) {
            checkSource = false;
            switch (photo.source) {
                case "PCHECK": case "COACHING":
                    if ('YES' === customerPlanningSetting.showCheckInQuestionnaire || 'PHOTO_ONLY' === customerPlanningSetting.showCra) {
                        checkSource = true;
                    } 
                    else if ('PUBLISHED' === customerPlanningSetting.showCheckInQuestionnaire) {
                        checkSource = photo.published;
                    }
                    break;
                    
                case "ANIMA_TAG_PHOTO": case "ANIMA_ISA_PHOTO": case "ANIMA_PRODUCT_PHOTO":
                    if ('YES' === customerPlanningSetting.showCra || 'PHOTO_ONLY' === customerPlanningSetting.showCra) {
                        checkSource = true;
                    } else if ('PUBLISHED' === customerPlanningSetting.showCra) {
                        checkSource = photo.published;
                    }
                    break;                    
                case "PEARLS":
                    checkSource = true;
                    break;  
            }
        }
        
        let checkFilters = ( (!this.areasIds || this.areasIds.indexOf(photo.customerAreaId) !== -1)
            && (!this.sectorsIds || this.sectorsIds.indexOf(photo.customerSectorId) !== -1)
            && (!this.city || (photo.storeCity && photo.storeCity.match(new RegExp(this.city,'gi')))) );
            
        if(this.keywordsCleaned?.length > 0){
            checkKeywords = this.checkSearchedKeywords(photo);
        }
                
        if(this.instantFiltersCleaned?.length > 0){
            checkInstantFilters = this.checkSearchedInstantPhotoTags(photo);
        }
        
        if(checkSource && checkKeywords){
            let count = 1;
            let instantFilterCount = this.instantFiltersCount.get(photo.instantPhotoTagCleaned);
            if(instantFilterCount !== undefined){
                count = (instantFilterCount+1);
            }          
            this.instantFiltersCount.set(photo.instantPhotoTagCleaned, count);              
        }
        
        return checkSource && checkFilters && checkKeywords && checkInstantFilters;
    }
    
    private checkSearchedKeywords(photo: Photo) {
        let matchesAnyKeywords = true;
        if (this.keywordsCleaned?.length > 0) {
            matchesAnyKeywords = false;
            for (let keyword of this.keywordsCleaned) {
                if ( (photo.tagNameCleaned && photo.tagNameCleaned.match(new RegExp(keyword,'gi'))) 
                    || (photo.modelNameCleaned && photo.modelNameCleaned.match(new RegExp(keyword,'gi'))) ){
                    matchesAnyKeywords = true;
                    break;
                }
            } 
        }
        return matchesAnyKeywords;
    }

    private checkSearchedInstantPhotoTags(photo: Photo) {
        let matchesAnyTag = true;
        if (this.instantFiltersCleaned?.length > 0) {
            matchesAnyTag = false;
            for (let tag of this.instantFiltersCleaned) {
              if (photo.instantPhotoTagCleaned && photo.instantPhotoTagCleaned.match(new RegExp(tag,'gi')) ) {
                matchesAnyTag = true;
                break;
              }
            }            
        }
        return matchesAnyTag;
    }
    
    cleanKeywords() {        
        let keywordsCleaned: string[] = [];
        if (this.keywords?.length > 0) {
            let value_norm = MarvStringService.RemoveAccents(this.keywords);
            keywordsCleaned = value_norm.split(/(\s+)/).filter((e: string) => {
                return e.trim().length > 0;
            });
        }
        this.keywordsCleaned = keywordsCleaned;                
    }
    
    static SplitList(inputArray: any[] | undefined, size: number) {
        if (inputArray) {
            const result = inputArray.reduce((resultArray: any[][], item: any, index: number) => {
                const chunkIndex = Math.floor(index / size)
                if (!resultArray[chunkIndex]) {
                  resultArray[chunkIndex] = [];
                }
                resultArray[chunkIndex].push(item);
                return resultArray;
            }, []);
            return result;
        }
        return [];
    }
    
    static IsNotEmptyCategory(category: PearlsPhotoProductCategory, dataProp: string){
        let hasData = false;
        category.ranges.forEach((range: any) => {
            if (!hasData){                
                hasData = range[dataProp].length > 0;
            }
        })
        return hasData;
    }
}

export class AnimaPhotoFiltersValues {
    
    photoTags: Array<number> = [];
    isaReportQuestions: Array<number> = [];
    productQuestions: Array<number> = [];
    products: Array<number> = [];
    skipTaggedPictures: boolean = false;
    skipIsaReportPictures: boolean = false;
    skipProductPictures: boolean = false;
        
    checkPictureTypes(logaPictureTypes: string[]) {
        if (this.skipTaggedPictures) {
            PhotoFiltersValues.removePictureType(logaPictureTypes, 'ANIMA_TAG_PHOTO');
        }
        if (this.skipIsaReportPictures) {
            PhotoFiltersValues.removePictureType(logaPictureTypes, 'ANIMA_ISA_PHOTO');
        }
        if (this.skipProductPictures) {
            PhotoFiltersValues.removePictureType(logaPictureTypes, 'ANIMA_PRODUCT_PHOTO');
        }
    }
    
}

export class PearlsPhotoFiltersValues {
    
    photoTags: Array<number> = [];
    products: Array<number> = [];
    productQuestions: Array<number> = [];
    posms: Array<number> = [];
    posmQuestions: Array<number> = [];
    storeCheckDataModels: Array<number> = [];
    
    skipTaggedPictures: boolean = false;
    skipProductPictures: boolean = false;
    skipPosmPictures: boolean = false;
    skipStoreCheckDataPictures: boolean = false;
    skipRangePictures: boolean = false;
    skipStitchedPhotos: boolean = false;
    skipTaskPictures: boolean = false;
    
}

@Component({
    selector: 'app-photo-gallery-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    imports: [
        IonContent, IonGrid, IonRow, IonCol, TranslatePipe, ReactiveFormsModule,
        forwardRef(() => MarvTabsComponent), forwardRef(() => MarvTabComponent), forwardRef(() => MarvButtonComponent),
        forwardRef(() => PhotoGalleryFiltersAllComponent), forwardRef(() => PhotoGalleryFiltersAnimaComponent), forwardRef(() => PhotoGalleryFiltersPearlsComponent)
    ]
})
export class PhotoGalleryFiltersComponent  implements OnInit {

    @Input({alias : 'operationsAll', required : true}) operationsAll: Array<OperationCard> = [];
    @Input({alias : 'photoFiltersValues', required : true}) photoFiltersValues: PhotoFiltersValues = new PhotoFiltersValues();
    @Input({alias : 'hideFiltersStore', required : false}) hideFiltersStore: boolean = false;
    @Output() onChangePhotoFilters = new EventEmitter<PhotoFiltersValues>();

    @ViewChild('photoGalleryFiltersAll') photoGalleryFiltersAll: PhotoGalleryFiltersAllComponent;
    @ViewChild('photoGalleryFiltersAnima') photoGalleryFiltersAnima: PhotoGalleryFiltersAnimaComponent;
    @ViewChild('photoGalleryFiltersPearls') photoGalleryFiltersPearls: PhotoGalleryFiltersPearlsComponent;

    tabsFilters: Array<MarvTab> = [
        {id: 'all', label: this.translateService.instant('ALL'), defaultSelected: true},       
        {id: 'anima', label: this.translateService.instant('ANIMATION_MERCH')},
        {id: 'pearls', label: this.translateService.instant('COMMANDO')}
    ];
    
    filtersPhotoForm: FormGroup = new FormGroup({
        startDate: new FormControl(null),
        endDate: new FormControl(null),
        typesMissionsCodes: new FormControl([]),
        operationsIds: new FormControl([]),
        areasIds: new FormControl([]),
        sectorsIds: new FormControl([]),
        agentsIds: new FormControl([]),
        retailersCodes: new FormControl([]),
        storesCodes: new FormControl([]),
        city: new FormControl(''),
        keywords: new FormControl(''),
        skipPresenceCheck: new FormControl(false),
        skipDrac: new FormControl(false),
        currentPearlsOperationId: new FormControl(null)
    });

    constructor(
        private translateService: TranslateService
    ) { }

    ngOnInit() {}
    
    async ngOnChanges(changes: SimpleChanges) {
        
        if(changes['photoFiltersValues'] && changes['photoFiltersValues'].currentValue){
            let values = {
                startDate: (this.photoFiltersValues.startDate ? this.photoFiltersValues.startDate : null), 
                endDate: (this.photoFiltersValues.endDate ? this.photoFiltersValues.endDate : null), 
                typesMissionsCodes: (this.photoFiltersValues.typesMissionsCodes?.length > 0 ? this.photoFiltersValues.typesMissionsCodes : []),
                operationsIds: (this.photoFiltersValues.operationsIds?.length > 0 ? this.photoFiltersValues.operationsIds : []),                
                areasIds: (this.photoFiltersValues.areasIds?.length > 0 ? this.photoFiltersValues.areasIds : []),
                sectorsIds: (this.photoFiltersValues.sectorsIds?.length > 0 ? this.photoFiltersValues.sectorsIds : []),
                agentsIds: (this.photoFiltersValues.agentsIds?.length > 0 ? this.photoFiltersValues.agentsIds : []),
                retailersCodes: (this.photoFiltersValues.retailersCodes?.length > 0 ? this.photoFiltersValues.retailersCodes : []),
                storesCodes: (this.photoFiltersValues.storesCodes?.length > 0 ? this.photoFiltersValues.storesCodes : []),
                city: (this.photoFiltersValues.city && this.photoFiltersValues.city != '' ? this.photoFiltersValues.city : ''),     
                keywords: (this.photoFiltersValues.keywords && this.photoFiltersValues.keywords != '' ? this.photoFiltersValues.keywords : ''),  
                skipPresenceCheck: (this.photoFiltersValues.skipPresenceCheck ? true : false),
                skipDrac: (this.photoFiltersValues.skipDrac ? true : false)
            };
            this.filtersPhotoForm.patchValue(values, {emitEvent: false});
            
        }
        
        if(changes['operationsAll'] && changes['operationsAll'].currentValue){
            
            let hideTabAnima = true;
            let hideTabPearls = true;
            
            this.operationsAll.forEach(o => {
                if(o.type === 'loga'){
                    hideTabAnima = false;
                }
                else if(o.type === 'lgg'){
                    hideTabPearls = false;
                }
            });
            
            let tabAnima = this.tabsFilters.find(t => t.id === 'anima');
            if(tabAnima){
                tabAnima.hide = hideTabAnima;
            }
            
            let tabPearls = this.tabsFilters.find(t => t.id === 'pearls');
            if(tabPearls){
                tabPearls.hide = hideTabPearls;
            }
        }
    }

    search() {
        if(this.filtersPhotoForm.valid){    
            let retailersItemsSelected = this.photoGalleryFiltersAll?.retailerSelect?.marvSelect?.getItemsSelected() || [];
            let storesItemsSelected = this.photoGalleryFiltersAll?.storeSelect?.marvSelect?.getItemsSelected() || [];
            let filtersValues = PhotoFiltersValues.FromForm(this.filtersPhotoForm, retailersItemsSelected, storesItemsSelected, this.photoGalleryFiltersAll?.instantPhotoFilterTags, this.photoGalleryFiltersAnima?.operationFilters, this.photoGalleryFiltersPearls?.photoSearchCriterias, this.photoFiltersValues.savingFilters, this.photoFiltersValues.viewSelected, this.photoFiltersValues.sizeSelected);
            filtersValues.saveFilters();        
            this.onChangePhotoFilters.emit(filtersValues);
        }
        else{
            for (var controlName in this.filtersPhotoForm.controls) {
                if(!this.filtersPhotoForm.controls[controlName].valid) {
                    this.filtersPhotoForm.controls[controlName].markAsTouched();
                }
            }
        }
    }
    
    displayAll() {
        let startDate = this.photoFiltersValues.startDate;
        let endDate = this.photoFiltersValues.endDate;
        let filters = new PhotoFiltersValues(false, this.photoFiltersValues.savingFilters, this.photoFiltersValues.viewSelected, this.photoFiltersValues.sizeSelected);
        filters.startDate = startDate;
        filters.endDate = endDate;
        filters.saveFilters();
        this.filtersPhotoForm.reset();
        this.onChangePhotoFilters.emit(filters);
    }
}
